import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from '../../app/api';

// Load list of notifications for the current user
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async () => {
        api.defaults.headers = {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
        };

        const response = await api.get('/notifications');
        const data = response.data;

        // Expect plain array or { items: [...] }
        if (Array.isArray(data)) {
            return data;
        }
        if (Array.isArray(data?.items)) {
            return data.items;
        }
        return [];
    }
);

const initialState = {
    items: [],
    isLoaded: false,
    error: null,
};

const coerceIdString = (value) => {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        // Keep only digits if it's a numeric-like identifier
        const digits = trimmed.match(/\d+/g)?.join('') || '';
        return digits || null;
    }
    return null;
};

const extractApplicationNumber = (raw) => {
    // Try common backend shapes for application id/number
    const candidates = [
        raw?.data?.application_id,
        raw?.data?.applicationId,
        raw?.data?.application_number,
        raw?.data?.applicationNumber,
        raw?.data?.application?.id,
        raw?.data?.application?.application_id,
        raw?.data?.application?.number,
        raw?.data?.entity_id,
        raw?.data?.entityId,
        raw?.data?.id,
    ];

    for (const c of candidates) {
        const v = coerceIdString(c);
        if (v) return v;
    }
    return null;
};

const extractTicketId = (raw) => {
    const candidates = [
        raw?.data?.ticket_id,
        raw?.data?.ticketId,
        raw?.data?.ticket?.id,
        raw?.data?.ticket?.ticket_id,
        raw?.data?.entity_id,
        raw?.data?.entityId,
        raw?.data?.id,
    ];
    for (const c of candidates) {
        const v = coerceIdString(c);
        if (v) return v;
    }
    return null;
};

const normalizeApplicationNumberInMessage = (message, applicationNumber) => {
    if (!message || !applicationNumber) return message || '';

    // If message contains an existing №<digits>, decide whether to replace it.
    const match = message.match(/№\s*(\d+)/);
    if (!match) return message;

    const msgNum = match[1];
    if (msgNum === applicationNumber) return message;

    // Heuristic: prefer our extracted application number when it looks like a small internal id
    // and the message contains a much larger identifier.
    const shouldReplace =
        (msgNum.length > 6 && applicationNumber.length <= 6) ||
        (Number(applicationNumber) > 0 && Number(applicationNumber) < 1_000_000);

    if (!shouldReplace) return message;

    return message.replace(/№\s*\d+/, `№${applicationNumber}`);
};

const mapNotification = (item) => {
    const dataType = item?.data?.type || item.type;
    let title = 'Уведомление';

    switch (dataType) {
        case 'application':
            title = 'Новое заявление';
            break;
        case 'application_status':
            title = 'Статус заявления изменён';
            break;
        case 'ticket':
            title = 'Новый тикет';
            break;
        case 'ticket_reply':
            title = 'Ответ на тикет';
            break;
        case 'ticket_status':
            title = 'Статус тикета изменён';
            break;
        case 'news':
            title = 'Новая новость';
            break;
        default:
            title = 'Уведомление';
    }

    return {
        id: item.__uid || item.id,
        backend_id: item.id,
        type: dataType,
        title,
        text: (() => {
            const rawMessage = item?.data?.message || '';
            const appNumber = extractApplicationNumber(item);
            const ticketId = extractTicketId(item);

            if (dataType === 'application' || dataType === 'application_status') {
                // Prefer a normalized "№{applicationId}" when payload provides it
                const normalized = normalizeApplicationNumberInMessage(rawMessage, appNumber);
                if (normalized) return normalized;

                if (appNumber) {
                    if (dataType === 'application') return `Новое заявление №${appNumber}`;
                    return `Статус вашего заявления №${appNumber} изменён`;
                }
            }

            if (dataType === 'ticket_status') {
                if (rawMessage) return rawMessage;
                if (ticketId) return `Статус тикета №${ticketId} изменён`;
                return 'Статус тикета изменён';
            }

            return rawMessage;
        })(),
        created_at: item.created_at,
        is_read: Boolean(item.read_at),
        link: (() => {
            const ticketId = extractTicketId(item);
            if ((dataType === 'ticket' || dataType === 'ticket_reply' || dataType === 'ticket_status') && ticketId) {
                return `/tickets/${ticketId}`;
            }
            return null;
        })(),
        // keep raw in case we need more later
        raw: item,
    };
};

const makeNotificationUid = (item, index) => {
    const backendId = item?.id != null ? String(item.id) : 'no-id';
    const createdAt =
        item?.created_at ||
        item?.data?.created_at ||
        item?.data?.create_at ||
        item?.data?.createdAt ||
        '';
    const type = item?.data?.type || item?.type || '';
    const message = item?.data?.message || item?.message || '';

    // Keep it readable/stable: no heavy hashing, just a composite key.
    // Index is used as last-resort tie-breaker if backend sends identical duplicates.
    return [backendId, createdAt, type, message, String(index)].join('|');
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Optimistic local mark-as-read; backend can be wired later if needed
        markAsReadLocally: (state, action) => {
            const id = action.payload;
            state.items = state.items.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        is_read: true,
                    }
                    : item
            );
        },
        clearNotifications: (state) => {
            state.items = [];
            state.isLoaded = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.isLoaded = false;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.isLoaded = true;
                const raw = Array.isArray(action.payload) ? action.payload : [];

                // NOTE:
                // Backend may reuse `id` for multiple notification events (e.g., per-entity).
                // Do NOT collapse by `id`, otherwise users may miss important notifications
                // like status updates. Instead, generate a stable unique uid for the UI.
                const withUid = raw
                    .filter(Boolean)
                    .map((item, idx) => ({ ...item, __uid: makeNotificationUid(item, idx) }));

                // Sort newest first when we have a timestamp
                withUid.sort((a, b) => {
                    const da = new Date(
                        a?.created_at || a?.data?.created_at || a?.data?.create_at || 0
                    ).getTime();
                    const db = new Date(
                        b?.created_at || b?.data?.created_at || b?.data?.create_at || 0
                    ).getTime();
                    return db - da;
                });

                state.items = withUid.map(mapNotification);
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.isLoaded = true;
                state.error = action.error?.message || 'Не удалось загрузить уведомления';
            });
    },
});

export const { markAsReadLocally, clearNotifications } = notificationsSlice.actions;

export const selectNotifications = (state) => state.notificationsSlice.items || [];
export const selectUnreadCount = (state) =>
    (state.notificationsSlice.items || []).filter(
        (n) => !n.is_read
    ).length;

export default notificationsSlice.reducer;
